import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { usePageContent, AccentText } from "@/lib/pageContent";
import CollaboratorInquiryModal, { openCollaboratorInquiry } from "@/components/CollaboratorInquiryModal";
import { TOPO_A } from "@/assets";

/* ---- exact landing tokens (match the section's original styling) ---------- */
const C = {
  page: "#050506", panel: "#111213", inset: "#0D0E0F", border: "#2E2F31", field: "#3E3F42",
  text: "#E8E8EA", muted: "#8B8D90", dim: "#6B6D70",
  olive: "#57682A", oliveLight: "#748A3D", lime: "#cfe86b", gold: "#E8C46A",
};
const PARTNERS_BG = "#141610";

const SectionLabel = ({ children }) => (
  <p className="font-mono text-[11px] tracking-[0.14em] uppercase">
    <span style={{ color: C.field }}>// </span>
    <span style={{ color: C.oliveLight }}>{children}</span>
  </p>
);

// The Partners page — the relocated homepage "Built Alongside the Best in the Outdoors" section,
// renamed Collaborator(s) → Partner(s). Content, styling and layout are unchanged from the section
// as it lived on the homepage; it now lives at its own /partners URL with a header nav entry.
export default function Partners() {
  const { get } = usePageContent("home");
  const [collaborators, setCollaborators] = useState([]);

  useEffect(() => {
    api.get("/collaborators").then(({ data }) => setCollaborators(data.collaborators || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen text-foreground" data-testid="partners-page">
      <Navbar />
      <section className="os-hairline os-topo scroll-mt-20" data-testid="collaborators-section" style={{ backgroundColor: PARTNERS_BG, "--os-topo-img": `url(${TOPO_A})` }}>
        <div className="max-w-[1240px] mx-auto px-5 py-20 grid min-[1000px]:grid-cols-2 gap-12 items-center">
          {/* left — pitch + CTA */}
          <div>
            <SectionLabel>{get("collab_eyebrow", "OpticSwap · Partners")}</SectionLabel>
            <h2 className="mt-2 font-heading font-bold uppercase tracking-tight leading-[1.08] text-2xl min-[1000px]:text-3xl" style={{ color: C.text }}>
              <AccentText text={get("collab_heading", "Built Alongside the Best in the Outdoors")} accentStyle={{ color: C.lime }} />
            </h2>
            <p className="mt-5 text-base max-w-[52ch] font-body" style={{ color: C.muted }}>
              {get("collab_body", "We work hand-in-hand with the brands, creators, and outfitters who share our standard for verified gear and straight dealing. Every partner here has helped shape how OpticSwap operates.")}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={openCollaboratorInquiry}
                data-testid="collaborators-cta"
                className="mkt-olive inline-flex items-center justify-center gap-2 font-mono uppercase text-[13px] tracking-[0.14em] px-6 py-3 transition-colors"
                style={{ backgroundColor: C.olive, color: C.text }}
              >
                {get("collab_cta", "Become a Partner")} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* right — Option D: 2-up partner tiles. Each tile is OPAQUE so the section's topo never
              bleeds through the box; the whole tile is a link that opens in a new tab. No caption —
              the logo alone carries it. */}
          <div>
            {collaborators.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 sm:gap-5" data-testid="collaborators-grid">
                {collaborators.map((c) => {
                  const linked = !!c.link_url;
                  const base = "group relative flex items-center justify-center aspect-square p-7 sm:p-9 bg-[#0C0D08] border";
                  const tileStyle = { borderColor: C.border };
                  const inner = (
                    <>
                      {linked && (
                        <ArrowUpRight className="absolute top-3 right-3 w-4 h-4 text-[#5c5e52] group-hover:text-[#cfe86b] transition-colors" />
                      )}
                      <img
                        src={c.logo_url}
                        alt={c.name}
                        className={`max-w-[84%] max-h-[84%] object-contain transition-transform duration-300 ${linked ? "group-hover:scale-[1.05]" : ""}`}
                      />
                    </>
                  );
                  return linked ? (
                    <a
                      key={c.collaborator_id}
                      href={c.link_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`collaborator-tile-${c.collaborator_id}`}
                      title={c.name}
                      aria-label={c.name}
                      className={`${base} hover:bg-[#12140C] transition-colors duration-300`}
                      style={tileStyle}
                    >
                      {inner}
                    </a>
                  ) : (
                    <div
                      key={c.collaborator_id}
                      data-testid={`collaborator-tile-${c.collaborator_id}`}
                      title={c.name}
                      aria-label={c.name}
                      className={base}
                      style={tileStyle}
                    >
                      {inner}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="font-mono uppercase text-[11px] tracking-[0.14em]" style={{ color: C.dim }} data-testid="collaborators-grid-empty">
                More partners coming soon.
              </p>
            )}
          </div>
        </div>
      </section>
      <CollaboratorInquiryModal />
    </div>
  );
}
